/**
 * Pure logic for the gap screens (brief §2.9), shared by the client and the Worker
 * (worker/src/dashboard-account.js imports it like navConfig.ts). Tested in tests/account.test.ts.
 */
import type { UserGroup } from "../nav/navConfig";

/* ---------- Onboarding (48–50) ---------- */

/** Step 3's topics (screenshots/50), in the design's order, with the words that match agenda titles. */
export const TOPICS: { label: string; words: RegExp }[] = [
  { label: "Housing", words: /\b(housing|dwelling|residential|affordable|landlord|tenant|rental|homeless)/i },
  { label: "Renters' rights", words: /\b(renters?|tenants?|landlords?|evictions?|rental|lease)\b/i },
  { label: "Parking", words: /\b(parking|meters?|loading zone|tow)/i },
  { label: "Public safety", words: /\b(police|public safety|fire department|violence|crime|emergency)/i },
  { label: "Budget & taxes", words: /\b(budget|tax(es)?|revenue|bonds?|appropriation|tif|levy|debt)\b/i },
  { label: "Street vending", words: /\b(vend(ors?|ing)|peddl|food trucks?|mobile food)/i },
  { label: "Schools", words: /\b(schools?|cps|education|students?)\b/i },
  { label: "Transit", words: /\b(transit|cta|bus(es)?|rail|bike|bicycle|pedestrian|traffic)/i },
  { label: "Immigration", words: /\b(immigra|welcoming city|migrants?|asylum|newcomers?)/i },
  { label: "Environment", words: /\b(environment|climate|emissions?|trees?|green|recycl|pollution|water)/i },
  { label: "Small business", words: /\b(small business|licens|liquor|package goods|business)/i },
  { label: "Zoning", words: /\b(zoning|rezon|landmark|planned development|variance)/i },
];

/** Which of the given topic labels a piece of text (an agenda item's title) matches. */
export function matchTopics(text: string, labels: string[]): string[] {
  return TOPICS.filter((t) => labels.includes(t.label) && t.words.test(text)).map((t) => t.label);
}

/**
 * How an onboarding submit maps onto our access rules (AGENT_BRIEF rule 4). Nothing grants access:
 *  - resident, not admitted     → request a resident account (approval only): POST /api/dashboard/request-access
 *  - office / advocacy          → ask the Chicago Politics team for a team account (it creates team accounts):
 *                                 POST /api/dashboard/request-access {group, workEmail | org}
 *  - the group you already have → confirm (admitted users)
 * The flow's own rules (an office needs a @cityofchicago.org work email, the street address never leaves the
 * browser) are in onboarding/onboardingFlow.ts.
 */
export type GroupAction = "confirm" | "request-resident" | "request-team";

export function groupAction(choice: UserGroup, current: UserGroup | null): GroupAction {
  if (current === choice) return "confirm";
  return choice === "resident" ? "request-resident" : "request-team";
}

/* ---------- Times (America/Chicago) ---------- */

const TZ = "America/Chicago";
const dayKey = (d: Date) => d.toLocaleDateString("en-CA", { timeZone: TZ });
const shortDate = (d: Date) => d.toLocaleDateString("en-US", { timeZone: TZ, month: "short", day: "numeric" });

function daysBetween(a: Date, b: Date) {
  return Math.round((Date.parse(dayKey(b)) - Date.parse(dayKey(a))) / 864e5);
}

/** Notification time column (53): "9:41 AM" today, "Yesterday", else "Oct 7". */
export function notificationTime(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const days = daysBetween(d, now);
  if (days <= 0) return d.toLocaleTimeString("en-US", { timeZone: TZ, hour: "numeric", minute: "2-digit" });
  if (days === 1) return "Yesterday";
  return shortDate(d);
}

/** Members table "Last active" (52): "Now", "2h ago", "Yesterday", "Oct 6". */
export function lastActiveLabel(iso: string | null, now = new Date()): string {
  if (!iso) return "";
  const d = new Date(iso), mins = (now.getTime() - d.getTime()) / 60e3;
  if (mins < 15) return "Now";
  if (mins < 24 * 60) return `${Math.max(1, Math.floor(mins / 60))}h ago`;
  if (daysBetween(d, now) <= 1) return "Yesterday";
  return shortDate(d);
}

/** Pending invite chip (52): "Invite sent Oct 7". */
export const inviteSentLabel = (iso: string) => `Invite sent ${shortDate(new Date(iso))}`;

/** Initials for the members table (52): "Alex Rivera" → "AR". */
export const initialsOf = (name: string) => name.split(/\s+/).filter(Boolean).map((w) => w[0]!.toUpperCase()).filter((_, i, a) => i === 0 || i === a.length - 1).join("");

/** "Anyone with @rentersrights.org can request to join": the domain of the team's owner. */
export const emailDomain = (email: string) => email.trim().toLowerCase().split("@")[1] ?? "";
